// SCR-CONSOLE-001 — فهرست مستأجرها (US-PLT-080): جستجو، فیلتر وضعیت و پلن، صفحه‌بندی Cursor و «ایجاد».
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type ConsoleTenantListItem, type TenantStatus } from '@phoenix/api-client';
import { usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, JalaliDate, ListPage, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { OwnershipApprovals } from './SubscriptionTab';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { usePlatformAccess } from '../../access';
import { accessModeTone, consoleKeys, tenantStatusTone } from '../../api';

export const SCR_CONSOLE_001 = 'SCR-CONSOLE-001';

const STATUSES: readonly TenantStatus[] = [
  'Provisioning',
  'ProvisioningFailed',
  'Active',
  'Suspended',
  'PendingTermination',
  'Terminated',
];

const PAGE_SIZE = 25;

function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function TenantListPage() {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const access = usePlatformAccess();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<TenantStatus | ''>('');
  const [plan, setPlan] = useState('');
  const q = useDebounced(search.trim());

  const filters = useMemo(
    () => ({ q: q || undefined, status: status || undefined, plan: plan || undefined }),
    [q, status, plan],
  );

  const tenants = useInfiniteQuery({
    queryKey: consoleKeys.tenants(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET('/api/v1/platform/console/tenants', {
          params: { query: { ...filters, cursor: pageParam, limit: PAGE_SIZE } },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  // فهرست پلن برای فیلتر فقط برای نقش‌های دارنده platform.plan.manage؛ بقیه کد پلن را تایپ می‌کنند.
  const canSeePlans = access.can('platform.plan.manage');
  const plans = useQuery({
    queryKey: consoleKeys.plans,
    queryFn: () => unwrap(api.GET('/api/v1/platform/console/plans')),
    enabled: canSeePlans,
  });

  const rows = tenants.data?.pages.flatMap((page) => page.items);

  const columns: DataTableColumn<ConsoleTenantListItem>[] = [
    {
      id: 'name',
      headerKey: 'console:tenants.columns.name',
      cell: (row) => (
        <RouterLink to={`/tenants/${row.id}`} data-testid={`tenant-link-${row.slug}`}>
          {row.displayName}
        </RouterLink>
      ),
    },
    { id: 'slug', headerKey: 'console:tenants.columns.slug', cell: (row) => <bdi dir="ltr">{row.slug}</bdi> },
    {
      id: 'centerType',
      headerKey: 'console:tenants.columns.center_type',
      cell: (row) => t(`console:center_type.${row.centerType}`),
    },
    {
      id: 'plan',
      headerKey: 'console:tenants.columns.plan',
      cell: (row) => (row.planCode ? <bdi dir="ltr">{row.planCode}</bdi> : t('common:states.none')),
    },
    {
      id: 'status',
      headerKey: 'console:tenants.columns.status',
      cell: (row) => (
        <StatusChip tone={tenantStatusTone(row.status)} labelKey={`console:tenant_status.${row.status}`} />
      ),
    },
    {
      id: 'accessMode',
      headerKey: 'console:tenants.columns.access_mode',
      cell: (row) => (
        <StatusChip
          tone={accessModeTone(row.accessMode)}
          labelKey={`console:access_mode.${row.accessMode}`}
        />
      ),
    },
    {
      id: 'createdAt',
      headerKey: 'console:tenants.columns.created_at',
      cell: (row) => <JalaliDate value={row.createdAt} />,
    },
  ];

  return (
    <ListPage
      screenId={SCR_CONSOLE_001}
      titleKey="console:tenants.list.title"
      subtitleKey="console:tenants.list.subtitle"
      actions={
        access.can('platform.tenant.create') ? (
          <Button
            component={RouterLink}
            to="/tenants/new"
            variant="contained"
            startIcon={<AddIcon />}
            data-testid="tenant-create"
          >
            {t('console:tenants.list.create')}
          </Button>
        ) : undefined
      }
    >
      {/* AP-PLT-07: انتقال‌های مالکیت منتظر تأیید پلتفرم (US-PLT-007). */}
      <Box sx={{ mb: 2 }}>
        <OwnershipApprovals />
      </Box>
      <DataTable
        captionKey="console:tenants.list.caption"
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        isLoading={tenants.isPending}
        error={tenants.error}
        onRetry={() => void tenants.refetch()}
        hasMore={tenants.hasNextPage}
        onLoadMore={() => void tenants.fetchNextPage()}
        isFetchingMore={tenants.isFetchingNextPage}
        onRowClick={(row) => void navigate(`/tenants/${row.id}`)}
        emptyTitleKey="console:tenants.list.empty_title"
        emptyDescriptionKey="console:tenants.list.empty_description"
        toolbar={
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField
              label={t('console:tenants.list.search')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: 260 }}
              slotProps={{ htmlInput: { 'data-testid': 'tenant-search' } }}
            />
            <TextField
              select
              label={t('console:tenants.list.filter_status')}
              value={status}
              onChange={(event) => setStatus(event.target.value as TenantStatus | '')}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">{t('console:tenants.list.all')}</MenuItem>
              {STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`console:tenant_status.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            {canSeePlans && plans.data ? (
              <TextField
                select
                label={t('console:tenants.list.filter_plan')}
                value={plan}
                onChange={(event) => setPlan(event.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">{t('console:tenants.list.all')}</MenuItem>
                {plans.data.items.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    <bdi dir="ltr">{item.code}</bdi>
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                label={t('console:tenants.list.filter_plan')}
                value={plan}
                onChange={(event) => setPlan(event.target.value.trim())}
                sx={{ minWidth: 180 }}
                slotProps={{ htmlInput: { dir: 'ltr' } }}
              />
            )}
          </Stack>
        }
      />
    </ListPage>
  );
}
